import {
  BasicModal,
  Box,
  Checkbox,
  Icon,
  Menu,
  MenuItem,
  Typography,
  useTheme
} from "@outschool/backpack";
import { faArrowRight, faChevronDown } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { searchPath } from "@outschool/routes";
import {
  useSelfElectedStateFundingProgram,
  useSession
} from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useLinkComponent, useNavigation } from "@outschool/ui-utils";
import React from "react";

export default function SelfIdentificationPublicFunding() {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding"
  );
  const { currentUser } = useSession();
  const theme = useTheme();
  const navigate = useNavigation();
  const Link = useLinkComponent();
  const {
    hideBanner,
    selfElectedStateFundingProgram,
    setState,
    setFundingOptions
  } = useSelfElectedStateFundingProgram();
  const [firstModalOpen, setFirstModalOpen] = React.useState(false);
  const [secondModalOpen, setSecondModalOpen] = React.useState(false);
  const [isBannerVisible, setIsBannerVisible] = React.useState(false);
  const stateMenuAnchorRef = React.useRef<HTMLDivElement>(null);
  const [stateMenuOpen, setStateMenuOpen] = React.useState(false);

  // Show banner after 1000ms delay
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsBannerVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleContinueToSecondModal = () => {
    setFirstModalOpen(false);
    // timeout after .25s to open second modal
    setTimeout(() => {
      setSecondModalOpen(true);
    }, 250);
  };

  const handleCloseModals = () => {
    setFirstModalOpen(false);
    setSecondModalOpen(false);
    // refresh page to set filters
    window.location.reload();
  };

  const handleNo = () => {
    hideBanner();
  };

  const handleYes = () => {
    setFirstModalOpen(true);
  };

  const handleCreateAccount = () => {
    setFirstModalOpen(false);
    setSecondModalOpen(false);
    hideBanner();
    navigate(searchPath(), { hardNav: true });
  };

  if (selfElectedStateFundingProgram.hideBanner || !!currentUser) {
    return null;
  }

  return (
    <>
      <Box
        flex
        sx={{
          background:
            "linear-gradient(195deg, rgba(56, 5, 150, 0.00) 16.68%, #380596 87.95%), #4B01D4",
          color: "primary.contrastText",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          paddingY: 2,
          paddingX: 3,
          textAlign: "center",
          position: "fixed",
          bottom: isBannerVisible ? 0 : -200,
          left: 0,
          zIndex: 1000,
          boxShadow: "0px -2px 8px rgba(0, 0, 0, 0.1)",
          minHeight: 80,
          transition: "bottom 0.5s ease-in-out",
          [theme.breakpoints.down("sm")]: {
            bottom: isBannerVisible ? 0 : -300
          }
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
            padding: 10,
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column",
              p: 10,
              gap: 5
            }
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              maxWidth: 900,
              textAlign: "left"
            }}
          >
            {t(
              "Are you planning to use state funding, such as an Education Savings Account (ESA), Micro-grant or scholarship to help pay for your learners education?"
            )}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignSelf: "flex-start",
              [theme.breakpoints.down(1000)]: {
                width: "100%",
                flexDirection: "column",
                gap: 5
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 10,
                alignSelf: "flex-start",
                [theme.breakpoints.down(1000)]: {
                  width: "100%",
                  flexDirection: "column",
                  gap: 5
                }
              }}
            >
              <TrackedButton
                variant="contained"
                trackingName="public_funding_self_identification_banner_yes"
                sx={{
                  borderColor: "white",
                  height: "32px",
                  borderWidth: 1,
                  width: 86,
                  [theme.breakpoints.down("sm")]: {
                    width: "100%",
                    marginY: 0
                  }
                }}
                onClick={handleYes}
              >
                {t("Yes")}
              </TrackedButton>
              <TrackedButton
                variant="contained"
                trackingName="public_funding_self_identification_banner_no"
                sx={{
                  borderColor: "white",
                  height: "32px",
                  borderWidth: 1,
                  width: 86,
                  [theme.breakpoints.down("sm")]: {
                    width: "100%",
                    marginY: 0
                  }
                }}
                onClick={handleNo}
              >
                {t("No")}
              </TrackedButton>
            </Box>
            <Box
              sx={{
                width: "1px",
                border: "1px solid white",
                borderWidth: "0 1px 0 0",
                height: 32,
                mx: 5,
                alignSelf: "flex-start",
                [theme.breakpoints.down(1000)]: {
                  display: "none"
                }
              }}
            />
            <TrackedButton
              variant="outlined"
              trackingName="public_funding_self_identification_banner_learn_more"
              sx={{
                borderColor: "white",
                height: "32px",
                display: "flex",
                alignItems: "center",
                alignSelf: "flex-start",
                "&:hover:not(:disabled)": {
                  borderColor: "transparent"
                },
                [theme.breakpoints.down(1000)]: {
                  width: "100%",
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  color: "white"
                }
              }}
              onClick={() => {
                navigate("/esa", {
                  hardNav: true,
                  newTab: true
                });
              }}
            >
              {t("Learn more")}
              <Icon icon={faArrowRight} sx={{ marginLeft: 5 }} />
            </TrackedButton>
          </Box>
        </Box>
      </Box>
      <BasicModal
        hasCloseButton
        closeButtonArialLabel={t("Close")}
        open={firstModalOpen}
        onClose={handleCloseModals}
        sx={{
          [theme.breakpoints.down("sm")]: {
            width: "100%",
            margin: 0
          },
          "& .MuiBox-root": {
            borderRadius: 15
          }
        }}
        modalCloseButtonProps={{
          // eslint-disable-next-line i18next/no-literal-string
          size: "large",
          sx: {
            fontSize: 24
          }
        }}
      >
        <Typography variant="h4" emphasized>
          {t("Which state is your Learner in?")}
        </Typography>
        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: 20,
            mt: 10,
            p: 20,
            backgroundColor: "primary.50",
            borderRadius: 10
          }}
        >
          <Typography variant="body1">
            {t(
              "Each state has different options for funding your child’s education."
            )}
          </Typography>
          <Typography variant="body1" emphasized>
            {t("Select your state to learn more.")}
          </Typography>
          <Box
            ref={stateMenuAnchorRef}
            flex
            sx={{
              cursor: "pointer",
              display: "flex",
              height: 56,
              padding: 12,
              px: 20,
              alignItems: "center",
              gap: 12,
              alignSelf: "stretch",
              borderRadius: 8,
              background: "white",
              boxShadow:
                "0 0 4px 0 rgba(0, 0, 0, 0.099),0 4px 8px 0 rgba(0, 0, 0,0.2)",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%"
            }}
            onClick={() => setStateMenuOpen(true)}
          >
            {selfElectedStateFundingProgram.stateName || t("Select your state")}
            <Icon icon={faChevronDown} sx={{ marginLeft: 5 }} />
          </Box>
          <Menu
            anchorEl={stateMenuAnchorRef.current}
            open={stateMenuOpen}
            onClose={() => setStateMenuOpen(false)}
            sx={{ width: 500 }}
          >
            <StateOptions
              width={stateMenuAnchorRef?.current?.clientWidth || 300}
              onStateSelect={(stateAbbreviation: string, stateName: string) => {
                // set the public funding state abbreviation in storage
                setState(stateAbbreviation, stateName, true);
                setStateMenuOpen(false);
              }}
              onClose={() => setStateMenuOpen(false)}
            />
          </Menu>
        </Box>
        <TrackedButton
          variant="contained"
          trackingName="public_funding_self_identification_state_modal_continue"
          onClick={handleContinueToSecondModal}
          disabled={!selfElectedStateFundingProgram.stateAbbreviation}
          sx={{
            width: "100%",
            mt: 20
          }}
        >
          {t("Show me my options")}
        </TrackedButton>
      </BasicModal>
      <BasicModal
        hasCloseButton
        closeButtonArialLabel={t("Close")}
        open={secondModalOpen}
        onClose={handleCloseModals}
        modalCloseButtonProps={{
          // eslint-disable-next-line i18next/no-literal-string
          size: "large",
          sx: {
            fontSize: 24
          }
        }}
        sx={{
          "& .MuiBox-root": {
            borderRadius: 15
          }
        }}
      >
        <Typography variant="h4" emphasized>
          {t("Tell us how you’ll fund your learner’s classes")}
        </Typography>
        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: 5,
            mt: 10,
            p: 20,
            backgroundColor: "primary.50",
            borderRadius: 10
          }}
        >
          <Typography variant="body1">
            {t(
              "Each state has different options for funding your child’s education."
            )}
          </Typography>
          <Typography variant="body1" emphasized>
            {t("State Funded Programs (ESA, micro-grants, scholarship)")}
          </Typography>
          <Typography variant="body1">
            {t(
              "Selecting a state program helps us personalize your experience, but it doesn’t guarantee coverage."
            )}
          </Typography>
          <Link
            trackingName="public_funding_self_identification_funding_options_learn_more"
            sx={{
              display: "flex",
              alignItems: "center",
              width: "fit-content"
            }}
            to="/esa"
            target="_blank"
          >
            {t("Learn more")}
            <Icon icon={faArrowRight} sx={{ marginLeft: 5 }} />
          </Link>
          <Box flex sx={{ flexDirection: "row", gap: 10, mt: 10 }}>
            <Checkbox
              sx={{
                width: "fit-content",
                alignSelf: "flex-start",
                py: 0
              }}
              id="statePortal"
              value={selfElectedStateFundingProgram.fundingOptions.statePortal}
              onChange={({ target }) => {
                // eslint-disable-next-line i18next/no-literal-string
                setFundingOptions("statePortal", target.checked);
              }}
            />
            <label
              htmlFor="statePortal"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                cursor: "pointer"
              }}
            >
              <Typography variant="body1">
                {t("Use my state portal to make purchases")}
              </Typography>
              <Typography variant="body2">
                {t(
                  "I make purchases through my state market place (Classwallet, Odyssey, Students First, Step Up For Students, etc.)."
                )}
              </Typography>
            </label>
          </Box>
          <Box flex sx={{ flexDirection: "row", gap: 10 }}>
            <Checkbox
              sx={{
                width: "fit-content",
                alignSelf: "flex-start",
                py: 0
              }}
              id="reimbursement"
              value={
                selfElectedStateFundingProgram.fundingOptions.reimbursement
              }
              onChange={({ target }) => {
                // eslint-disable-next-line i18next/no-literal-string
                setFundingOptions("reimbursement", target.checked);
              }}
            />
            <label
              htmlFor="reimbursement"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                cursor: "pointer"
              }}
            >
              <Typography variant="body1">
                {t("Reimbursement through my state program")}
              </Typography>
              <Typography variant="body2">
                {t(
                  "I submit receipts for reimbursement from my state program."
                )}
              </Typography>
            </label>
          </Box>
          <Typography variant="body1" emphasized sx={{ mt: 10 }}>
            {t("Other funding options")}
          </Typography>
          <Box flex sx={{ flexDirection: "row", gap: 10 }}>
            <Checkbox
              sx={{
                width: "fit-content",
                alignSelf: "flex-start",
                py: 0
              }}
              id="charter"
              value={selfElectedStateFundingProgram.fundingOptions.charter}
              onChange={({ target }) => {
                // eslint-disable-next-line i18next/no-literal-string
                setFundingOptions("charter", target.checked);
              }}
            />
            <label
              htmlFor="charter"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                cursor: "pointer"
              }}
            >
              <Typography variant="body1">
                {t("Charter School Funding")}
              </Typography>
              <Typography variant="body2">
                {t(
                  "My charter school purchases Outschool enrollments for my learner."
                )}
              </Typography>
            </label>
          </Box>
          <Box flex sx={{ flexDirection: "row", gap: 10 }}>
            <Checkbox
              sx={{
                width: "fit-content",
                alignSelf: "flex-start",
                py: 0
              }}
              id="selfPay"
              value={selfElectedStateFundingProgram.fundingOptions.selfPay}
              onChange={({ target }) => {
                // eslint-disable-next-line i18next/no-literal-string
                setFundingOptions("selfPay", target.checked);
              }}
            />
            <label
              htmlFor="selfPay"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                cursor: "pointer"
              }}
            >
              <Typography variant="body1">{t("Self-pay")}</Typography>
              <Typography variant="body2">
                {t("I plan on paying for Outschool myself.")}
              </Typography>
            </label>
          </Box>
        </Box>
        <TrackedButton
          variant="contained"
          trackingName="public_funding_self_identification_funding_options_continue"
          onClick={handleCreateAccount}
          sx={{
            width: "100%",
            mt: 20
          }}
        >
          {t("Browse eligible classes now")}
        </TrackedButton>
      </BasicModal>
    </>
  );
}

const StateOptions = ({
  onStateSelect,
  onClose,
  width
}: {
  onStateSelect: (stateAbbreviation: string, stateName: string) => void;
  onClose: () => void;
  width: number;
}) => {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding"
  );
  const states = [
    { name: t("Alabama"), abbreviation: "AL" },
    { name: t("Alaska"), abbreviation: "AK" },
    { name: t("Arizona"), abbreviation: "AZ" },
    { name: t("Arkansas"), abbreviation: "AR" },
    { name: t("California"), abbreviation: "CA" },
    { name: t("Colorado"), abbreviation: "CO" },
    { name: t("Connecticut"), abbreviation: "CT" },
    { name: t("Delaware"), abbreviation: "DE" },
    { name: t("Florida"), abbreviation: "FL" },
    { name: t("Georgia"), abbreviation: "GA" },
    { name: t("Hawaii"), abbreviation: "HI" },
    { name: t("Idaho"), abbreviation: "ID" },
    { name: t("Illinois"), abbreviation: "IL" },
    { name: t("Indiana"), abbreviation: "IN" },
    { name: t("Iowa"), abbreviation: "IA" },
    { name: t("Kansas"), abbreviation: "KS" },
    { name: t("Kentucky"), abbreviation: "KY" },
    { name: t("Louisiana"), abbreviation: "LA" },
    { name: t("Maine"), abbreviation: "ME" },
    { name: t("Maryland"), abbreviation: "MD" },
    { name: t("Massachusetts"), abbreviation: "MA" },
    { name: t("Michigan"), abbreviation: "MI" },
    { name: t("Minnesota"), abbreviation: "MN" },
    { name: t("Mississippi"), abbreviation: "MS" },
    { name: t("Missouri"), abbreviation: "MO" },
    { name: t("Montana"), abbreviation: "MT" },
    { name: t("Nebraska"), abbreviation: "NE" },
    { name: t("Nevada"), abbreviation: "NV" },
    { name: t("New Hampshire"), abbreviation: "NH" },
    { name: t("New Jersey"), abbreviation: "NJ" },
    { name: t("New Mexico"), abbreviation: "NM" },
    { name: t("New York"), abbreviation: "NY" },
    { name: t("North Carolina"), abbreviation: "NC" },
    { name: t("North Dakota"), abbreviation: "ND" },
    { name: t("Ohio"), abbreviation: "OH" },
    { name: t("Oklahoma"), abbreviation: "OK" },
    { name: t("Oregon"), abbreviation: "OR" },
    { name: t("Pennsylvania"), abbreviation: "PA" },
    { name: t("Rhode Island"), abbreviation: "RI" },
    { name: t("South Carolina"), abbreviation: "SC" },
    { name: t("South Dakota"), abbreviation: "SD" },
    { name: t("Tennessee"), abbreviation: "TN" },
    { name: t("Texas"), abbreviation: "TX" },
    { name: t("Utah"), abbreviation: "UT" },
    { name: t("Vermont"), abbreviation: "VT" },
    { name: t("Virginia"), abbreviation: "VA" },
    { name: t("Washington"), abbreviation: "WA" },
    { name: t("West Virginia"), abbreviation: "WV" },
    { name: t("Wisconsin"), abbreviation: "WI" },
    { name: t("Wyoming"), abbreviation: "WY" }
  ];

  return (
    <>
      {states.map((state, index) => (
        <MenuItem
          key={index}
          onClick={() => {
            onStateSelect(state.abbreviation, state.name);
            onClose();
          }}
          sx={{ width }}
        >
          <Typography variant="body1" sx={{ px: 10 }}>
            {state.name}
          </Typography>
        </MenuItem>
      ))}
    </>
  );
};
